import { create } from 'zustand';

export interface EstablishmentData {
  socialMedia?: SocialMedia;
  branding?: Branding;
  weeklyOperatingHours?: Record<string, { open: string; close: string }>;
  specialOperatingDays?: Array<{ date: string; open: string; close: string }>;
  closedDays?: Array<{ date: string; reason?: string }>;
  regularSchedule?: DaySchedule[];
  specialSchedules?: SpecialSchedule[];
  exceptionDates?: ExceptionDate[];
}

export interface SocialMedia {
  facebook?: string;
  instagram?: string;
  whatsapp?: string;
  twitter?: string;
  linkedin?: string;
  tiktok?: string;
  youtube?: string;
  website?: string;
}

export interface DaySchedule {
  day: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
  enabled: boolean;
  openTime: string;
  closeTime: string;
}

export interface SpecialSchedule {
  id: string;
  date: string;
  openTime: string;
  closeTime: string;
}

export interface ExceptionDate {
  id: string;
  date: string;
  reason?: string;
}

export interface Branding {
  logo?: string;
  slogan?: string;
  primaryColor?: string;
  secondaryColor?: string;
}

interface SettingsState {
  socialMedia: SocialMedia;
  branding: Branding;
  regularSchedule: DaySchedule[];
  specialSchedules: SpecialSchedule[];
  exceptionDates: ExceptionDate[];
  isDataLoaded: boolean; // Flag para controlar se os dados já foram carregados do backend

  // Social Media Actions
  setSocialMedia: (socialMedia: SocialMedia) => void;
  updateSocialMedia: (platform: keyof SocialMedia, value: string) => void;

  // Branding Actions
  setBranding: (branding: Branding) => void;
  updateBranding: (field: keyof Branding, value: string) => void;

  // Regular Schedule Actions
  setRegularSchedule: (schedule: DaySchedule[]) => void;
  updateDaySchedule: (day: DaySchedule['day'], updates: Partial<DaySchedule>) => void;

  // Special Schedule Actions
  setSpecialSchedules: (schedules: SpecialSchedule[]) => void;
  addSpecialSchedule: (schedule: Omit<SpecialSchedule, 'id'>) => void;
  updateSpecialSchedule: (id: string, updates: Partial<SpecialSchedule>) => void;
  removeSpecialSchedule: (id: string) => void;

  // Exception Dates Actions
  setExceptionDates: (dates: ExceptionDate[]) => void;
  addExceptionDate: (exception: Omit<ExceptionDate, 'id'>) => void;
  updateExceptionDate: (id: string, updates: Partial<ExceptionDate>) => void;
  removeExceptionDate: (id: string) => void;

  // Data loaded flag
  markDataAsLoaded: () => void;
  resetDataLoaded: () => void;

  // Clear all
  clearAllSettings: () => void;

  // Load from establishment data
  loadFromEstablishment: (establishmentData: EstablishmentData) => void;
}

// Schedule vazio (sem valores padrão)
const emptyRegularSchedule: DaySchedule[] = [
  { day: 'monday', enabled: false, openTime: '', closeTime: '' },
  { day: 'tuesday', enabled: false, openTime: '', closeTime: '' },
  { day: 'wednesday', enabled: false, openTime: '', closeTime: '' },
  { day: 'thursday', enabled: false, openTime: '', closeTime: '' },
  { day: 'friday', enabled: false, openTime: '', closeTime: '' },
  { day: 'saturday', enabled: false, openTime: '', closeTime: '' },
  { day: 'sunday', enabled: false, openTime: '', closeTime: '' },
];

export const useSettingsStore = create<SettingsState>()((set) => ({
  socialMedia: {},
  branding: {},
  regularSchedule: emptyRegularSchedule,
  specialSchedules: [],
  exceptionDates: [],
  isDataLoaded: false,

  // Social Media Actions
  setSocialMedia: (socialMedia) => set({ socialMedia }),

  updateSocialMedia: (platform, value) =>
    set((state) => ({
      socialMedia: {
        ...state.socialMedia,
        [platform]: value,
      },
    })),

  // Branding Actions
  setBranding: (branding) => set({ branding }),

  updateBranding: (field, value) =>
    set((state) => ({
      branding: {
        ...state.branding,
        [field]: value,
      },
    })),

  // Regular Schedule Actions
  setRegularSchedule: (schedule) => set({ regularSchedule: schedule }),

  updateDaySchedule: (day, updates) =>
    set((state) => ({
      regularSchedule: state.regularSchedule.map((schedule) =>
        schedule.day === day ? { ...schedule, ...updates } : schedule
      ),
    })),

  // Special Schedule Actions
  setSpecialSchedules: (schedules) => set({ specialSchedules: schedules }),

  addSpecialSchedule: (schedule) =>
    set((state) => ({
      specialSchedules: [...state.specialSchedules, { ...schedule, id: Date.now().toString() }],
    })),

  updateSpecialSchedule: (id, updates) =>
    set((state) => ({
      specialSchedules: state.specialSchedules.map((schedule) =>
        schedule.id === id ? { ...schedule, ...updates } : schedule
      ),
    })),

  removeSpecialSchedule: (id) =>
    set((state) => ({
      specialSchedules: state.specialSchedules.filter((schedule) => schedule.id !== id),
    })),

  // Exception Dates Actions
  setExceptionDates: (dates) => set({ exceptionDates: dates }),

  addExceptionDate: (exception) =>
    set((state) => ({
      exceptionDates: [...state.exceptionDates, { ...exception, id: Date.now().toString() }],
    })),

  updateExceptionDate: (id, updates) =>
    set((state) => ({
      exceptionDates: state.exceptionDates.map((exception) =>
        exception.id === id ? { ...exception, ...updates } : exception
      ),
    })),

  removeExceptionDate: (id) =>
    set((state) => ({
      exceptionDates: state.exceptionDates.filter((exception) => exception.id !== id),
    })),

  // Mark data as loaded
  markDataAsLoaded: () => set({ isDataLoaded: true }),

  // Reset so useEstablishmentData re-syncs from fresh GET (e.g. on Settings page mount)
  resetDataLoaded: () => set({ isDataLoaded: false }),

  // Clear all
  clearAllSettings: () =>
    set({
      socialMedia: {},
      branding: {},
      regularSchedule: emptyRegularSchedule,
      specialSchedules: [],
      exceptionDates: [],
      isDataLoaded: false,
    }),

  // Load from establishment data
  loadFromEstablishment: (establishmentData) => {
    const newState: Partial<SettingsState> = {};

    // Load social media
    if (establishmentData.socialMedia) {
      newState.socialMedia = establishmentData.socialMedia;
    }

    // Load branding
    if (establishmentData.branding) {
      newState.branding = establishmentData.branding;
    }

    // Load regular schedule
    if (establishmentData.regularSchedule && Array.isArray(establishmentData.regularSchedule)) {
      newState.regularSchedule = establishmentData.regularSchedule;
    }

    // Load special schedules
    if (establishmentData.specialSchedules && Array.isArray(establishmentData.specialSchedules)) {
      newState.specialSchedules = establishmentData.specialSchedules;
    }

    // Load exception dates
    if (establishmentData.exceptionDates && Array.isArray(establishmentData.exceptionDates)) {
      newState.exceptionDates = establishmentData.exceptionDates;
    }

    set(newState);
  },
}));
